import { Box, CssBaseline, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Outlet, useLocation } from 'react-router-dom';
// import XIcon from '@mui/icons-material/X';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Header, PublicFooter } from './components';

const Main = styled('div')(({ theme }) => ({
    ...theme.typography.mainContent,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    minHeight: '10vh',
    marginLeft: 0
}));

const PublicLayout = () => {
    const theme = useTheme();
    const location = useLocation();
    const mainRef = useRef<HTMLDivElement>(null);
    const prevScrollRef = useRef<number>(0);
    const drawerOpenRef = useRef<boolean>(true);
    const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
    const [drawerOpen, setDrawerOpen] = useState<boolean>(true);
    const [headerHeight, setHeaderHeight] = useState<number>(lgUp ? 175 : 180);
    const [clubsScrollState, setClubsScrollState] = useState({ canScroll: false, progress: 0, thumbSize: 100 });
    const isCalendarRoute = location.pathname.includes('/public/calendar');
    const isCompetitionsMapRoute = location.pathname === '/public/competitions-map';
    const isCompetitionDetailsRoute = location.pathname === '/public/competition';
    const isClubsRoute = location.pathname.startsWith('/public/clubs');
    const isClubsListRoute = location.pathname === '/public/clubs';

    useEffect(() => {
        drawerOpenRef.current = drawerOpen;
    }, [drawerOpen]);

    const handleHeaderHeightChange = useCallback((height: number) => {
        setHeaderHeight((previousHeight) => (previousHeight === height ? previousHeight : height));
    }, []);

    useEffect(() => {
        const mainElement = mainRef.current;
        if (!mainElement) return;

        const updateClubsScrollState = () => {
            if (!isClubsListRoute) return;

            const scrollableHeight = mainElement.scrollHeight - mainElement.clientHeight;
            const canScroll = scrollableHeight > 4;
            const progress = canScroll ? mainElement.scrollTop / scrollableHeight : 0;
            const thumbSize = canScroll ? Math.max(12, (mainElement.clientHeight / mainElement.scrollHeight) * 100) : 100;

            setClubsScrollState({ canScroll, progress, thumbSize });
        };

        updateClubsScrollState();

        if (isCalendarRoute || isCompetitionsMapRoute || isCompetitionDetailsRoute) {
            if (!drawerOpenRef.current) setDrawerOpen(true);
            return;
        }

        const mutationObserver =
            isClubsListRoute && typeof MutationObserver !== 'undefined'
                ? new MutationObserver(() => {
                      updateClubsScrollState();
                  })
                : null;

        mutationObserver?.observe(mainElement, {
            childList: true,
            subtree: true,
            attributes: true
        });

        const handleScroll = () => {
            const scrollContainer = mainRef.current;
            if (!scrollContainer) return;

            updateClubsScrollState();

            const scrollableHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
            const currentScroll = scrollContainer.scrollTop || 0;

            if (scrollableHeight <= 0) {
                if (!drawerOpenRef.current) setDrawerOpen(true);
                prevScrollRef.current = 0;
                return;
            }

            if (isClubsRoute) {
                if (!drawerOpenRef.current) setDrawerOpen(true);
                prevScrollRef.current = currentScroll;
                return;
            }

            const viewportHeight = typeof window === 'undefined' ? 0 : window.innerHeight;
            const minScrollableHeightForHeaderToggle = Math.max(96, Math.round(viewportHeight * (lgUp ? 0.18 : 0.26)));

            if (scrollableHeight < minScrollableHeightForHeaderToggle) {
                if (!drawerOpenRef.current) setDrawerOpen(true);
                prevScrollRef.current = currentScroll;
                return;
            }

            const delta = currentScroll - prevScrollRef.current;
            const toggleThreshold = 10;
            const openNearTopThreshold = 24;

            if (currentScroll <= openNearTopThreshold) {
                if (!drawerOpenRef.current) setDrawerOpen(true);
            } else if (delta > toggleThreshold && drawerOpenRef.current) {
                setDrawerOpen(false);
            } else if (delta < -toggleThreshold && !drawerOpenRef.current) {
                setDrawerOpen(true);
            }

            prevScrollRef.current = currentScroll;
        };

        mainElement.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            mainElement.removeEventListener('scroll', handleScroll);
            mutationObserver?.disconnect();
        };
    }, [isCalendarRoute, isCompetitionsMapRoute, isCompetitionDetailsRoute, isClubsListRoute, isClubsRoute, lgUp]);

    const mainMarginTop = useMemo(() => `${headerHeight}px`, [headerHeight]);

    const mainHeight = useMemo(() => `calc(100vh - ${headerHeight}px)`, [headerHeight]);

    return (
        <Box sx={{ height: '100vh', overflow: 'hidden', pb: 2 }}>
            <CssBaseline />
            <Header open={drawerOpen} onHeightChange={handleHeaderHeightChange} />
            {isClubsListRoute && clubsScrollState.canScroll && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: mainMarginTop,
                        right: 8,
                        height: mainHeight,
                        width: 12,
                        borderRadius: 999,
                        background: '#edf3f8',
                        border: '1px solid #d7e3ef',
                        boxShadow: '0 4px 12px rgba(36, 76, 118, 0.18)',
                        zIndex: 1600,
                        pointerEvents: 'none'
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 2,
                            right: 2,
                            top: `calc((100% - ${clubsScrollState.thumbSize}%) * ${clubsScrollState.progress})`,
                            height: `${clubsScrollState.thumbSize}%`,
                            minHeight: 36,
                            borderRadius: 999,
                            background: '#5f86ad',
                            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.35)'
                        }}
                    />
                </Box>
            )}
            <Main
                id="public-main-scroll-container"
                ref={mainRef}
                sx={{
                    background: theme.palette.background.default,
                    mt: mainMarginTop,
                    boxSizing: 'content-box',
                    overflowY: isClubsListRoute ? 'scroll' : 'auto',
                    height: mainHeight,
                    display: 'flex',
                    flexDirection: 'column',
                    width: lgUp ? '100%' : '94%',
                    ...(isClubsListRoute && {
                        scrollbarGutter: 'stable',
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#8aa7c7 #edf3f8',
                        '&::-webkit-scrollbar': {
                            width: 10
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#edf3f8',
                            borderRadius: 10
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#8aa7c7',
                            borderRadius: 10,
                            border: '2px solid #edf3f8'
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: '#5f86ad'
                        }
                    })
                }}
                theme={theme}
            >
                <Outlet />
                <PublicFooter desktop={lgUp} />
            </Main>
        </Box>
    );
};

export default PublicLayout;
